import { HttpParams } from "@angular/common/http";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { cloneDeep } from "lodash";
import { ConfirmationService, TreeNode } from "primeng/api";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { Subject, takeUntil } from "rxjs";
import { MoveDialogComponent } from "src/app/common/components/floating-assistant-chat/move-dialog/move-dialog.component";
import { PromptEditorComponent } from "src/app/common/components/floating-assistant-chat/prompt-editor/prompt-editor.component";
import { PromptNodeEditorComponent } from "src/app/common/components/floating-assistant-chat/prompt-node-editor/prompt-node-editor.component";
import { AclService } from "src/app/common/services/acl.service";
import { FloatingAssistantChatService } from "src/app/common/services/floating-assistant-chat.service";
import {
  NamedPromptCategory,
  NamedPromptCategoryService,
  NamedPromptCategoryValue,
} from "src/app/common/services/named-prompt-category.service";
import { NotificationService } from "src/app/common/services/notification.service";
import { PromptTreeService } from "src/app/common/services/prompt-tree.service";
import { copyStringToClipboard } from "src/app/common/tools/copyHtmlOfElement";
import { PredefinedPrompt } from "src/app/modules/prompts/models/prompt-template.model";
import { PromptsService } from "src/app/modules/prompts/services/prompts.service";

@Component({
  templateUrl: "./prompt-selector.component.html",
  selector: "app-prompt-selector",
  styleUrls: ["./prompt-selector.component.scss"],
})
export class PromptSelectorComponent implements OnInit, OnDestroy {
  private readonly promptsService = inject(PromptsService);
  private readonly floatingAssistantChatService = inject(FloatingAssistantChatService);
  private readonly destroySubject = new Subject<void>();
  private readonly confirmationService = inject(ConfirmationService);
  private readonly notificationService = inject(NotificationService);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly dialogService = inject(DialogService);
  private canModifyTemplatePrompts = false;
  private readonly aclService = inject(AclService);
  private readonly namedPromptCategoryService = inject(NamedPromptCategoryService);
  private readonly promptTreeService = inject(PromptTreeService);
  private readonly modifiablePromptNodes: string[] = [];

  @Input() disabled = false;
  @Input() isReadOnly = false;
  @Input() selectedPrompt?: TreeNode<PredefinedPrompt>;
  @Input({ required: true }) isFloattingAssistantChat = false;
  @Output() readonly promptSelected = new EventEmitter<{ node: TreeNode<PredefinedPrompt> }>();
  @Output() readonly promptCleared = new EventEmitter();

  protected predefinedPrompts: TreeNode<PredefinedPrompt>[] = [];
  private promptEditorDialogRef: DynamicDialogRef | undefined;

  protected readonly PromptNode!: TreeNode<PredefinedPrompt>;
  protected readonly categories = this.namedPromptCategoryService.namedPromptCategories;
  protected readonly favoriteNodeKey = "favorites"; // not the label, used to identify the node
  protected readonly myPromptsNodeCategory: string = "MES_PROMPTS";

  async ngOnInit(): Promise<void> {
    await this.setPredefinedPrompts();
    await this.setModifiablePromptNodes();

    if (this.isFloattingAssistantChat) {
      this.floatingAssistantChatService.setPredefinedPromptByLabel.pipe(takeUntil(this.destroySubject)).subscribe({
        next: (label: string) => {
          const stack = [...this.predefinedPrompts];
          while (stack.length > 0) {
            const prompt = stack.pop();
            if (prompt?.label === label && prompt.data) {
              this.preselectPrompt(prompt);
              return;
            }
            if (prompt?.children && prompt.children.length > 0) {
              stack.push(...prompt.children);
            }
          }
        },
      });
    }
    this.promptsService.predefinedPromptsChanged.pipe(takeUntil(this.destroySubject)).subscribe({
      next: () => {
        void (async () => {
          await this.setPredefinedPrompts();
          await this.setModifiablePromptNodes();
        })();
      },
    });

    // If preselectedPromptId is defined, search and select the corresponding prompt
    if (!this.isFloattingAssistantChat) {
      this.activatedRoute.queryParams.pipe(takeUntil(this.destroySubject)).subscribe((params) => {
        const preselectedPromptId = params["promptId"];
        const stack = [...this.predefinedPrompts];
        while (stack.length > 0) {
          const prompt = stack.pop();
          if (prompt?.key === preselectedPromptId && prompt?.data) {
            this.preselectPrompt(prompt);
            return;
          }
          if (prompt?.children && prompt.children.length > 0) {
            stack.push(...prompt.children);
          }
        }
      });
    }
  }

  private preselectPrompt(prompt: TreeNode<PredefinedPrompt>): void {
    if (!prompt?.data) {
      return;
    }
    this.selectedPrompt = prompt;
    this.promptSelected.emit({
      node: {
        label: prompt.data.label,
        data: {
          id: prompt.data.id,
          label: prompt.data.label,
          userPrompt: prompt.data.userPrompt,
          systemPrompt: prompt.data.systemPrompt,
          temperature: prompt.data.temperature,
          model: prompt.data.model,
          order: prompt.data.order,
          contextFiles: prompt.data.contextFiles,
          searchGrounding: prompt.data.searchGrounding,
        },
      },
    });
  }

  protected canSeeEditButtons(node?: TreeNode<PredefinedPrompt>): boolean {
    return this.canModifyTemplatePrompts || this.isModifiable(node);
  }

  private isModifiable(node?: TreeNode<PredefinedPrompt>): boolean {
    while (node) {
      if (node.key && this.modifiablePromptNodes.includes(node.key)) {
        return true;
      }
      node = node.parent;
    }
    return false;
  }

  protected canSeeDeleteButtons(node: TreeNode<PredefinedPrompt>): boolean {
    let parent = node.parent;
    while (parent) {
      if (["SUMMARY", "IAPP"].includes(parent.data?.label ?? "")) {
        return false;
      }
      parent = parent.parent;
    }
    return true;
  }

  protected openPromptNodeEditor(node: TreeNode<PredefinedPrompt> | undefined, action: "add" | "edit"): void {
    const header =
      action === "add"
        ? $localize`:@@prompt_selector_add_node_title:Ajouter un nouveau noeud à ${node?.label}`
        : $localize`:@@prompt_selector_edit_node_title:Éditer le noeud ${node?.label}`;
    const promptNodeEditorDialogRef = this.dialogService.open(PromptNodeEditorComponent, {
      header,
      width: "500px",
      data: {
        node,
        action,
      },
    });

    promptNodeEditorDialogRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((n) => {
      if (n) {
        this.promptsService.predefinedPromptsChanged.emit();
        const successMessage =
          action === "add"
            ? $localize`:@@prompt_selector_sucessfully_added_node_message:Noeud ajouté avec succès`
            : $localize`:@@prompt_selector_sucessfully_edited_node_message:Noeud édité avec succès`;
        this.notificationService.showInfo(successMessage);
      }
    });
  }

  protected canSeeAddNodeButtons(node?: TreeNode<PredefinedPrompt>): boolean {
    if (!node?.data?.label || !node.data?.id) {
      return false;
    }
    const canModify = this.canModifyTemplatePrompts || this.isModifiable(node);
    const currentDepth = node.data.depth ?? 0;
    if (this.isParentMyPrompts(node)) {
      return currentDepth < 1 && canModify;
    }
    return currentDepth < 2 && canModify;
  }

  protected canSeeEditNodeButtons(node?: TreeNode<PredefinedPrompt>): boolean {
    if (!node?.data?.label || !node.data?.id || this.isMyPrompts(node)) {
      return false;
    }
    const nonEditableCategories = Object.values(this.categories).map((c: NamedPromptCategoryValue) => c.key);
    const canModify =
      this.canModifyTemplatePrompts || (!nonEditableCategories.includes(node.data.label) && this.isModifiable(node));
    const currentDepth = node.data.depth ?? 0;
    return currentDepth < 3 && canModify;
  }

  protected canSeeDeleteNodeButtons(node?: TreeNode<PredefinedPrompt>): boolean {
    const isNodeEmpty = node?.children?.length === 0;
    return !this.isMyFavorites(node) && isNodeEmpty && this.canSeeEditNodeButtons(node);
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  private async checkForModifiablePromptNodes(node: TreeNode<PredefinedPrompt>): Promise<Set<string>> {
    // Check if the option to modify all prompts is checked
    // This is a normal role and is not handled by promptRoles
    this.canModifyTemplatePrompts = await this.aclService.isAuthorized("MODIFY_TEMPLATE_PROMPTS");

    const modifiablePrompts = new Set<string>();

    // Always add the "my prompts" node
    if (node.key && node.data?.label === "MES_PROMPTS") {
      modifiablePrompts.add(node.key);
    }
    if (node.key && (await this.aclService.canAccessPrompt(node.key))) {
      modifiablePrompts.add(node.key);
    }
    if (node.children) {
      for (const child of node.children) {
        const childPrompts = await this.checkForModifiablePromptNodes(child);
        for (const prompt of childPrompts) {
          modifiablePrompts.add(prompt);
        }
      }
    }
    return modifiablePrompts;
  }

  private async setModifiablePromptNodes(): Promise<void> {
    this.modifiablePromptNodes.length = 0;
    for (const node of this.predefinedPrompts) {
      const updatedNodeIds = Array.from(await this.checkForModifiablePromptNodes(node));
      this.modifiablePromptNodes.push(...updatedNodeIds);
    }
  }

  private async setPredefinedPrompts(): Promise<void> {
    this.predefinedPrompts.length = 0;
    const promptModels = await this.promptsService.listTemplates(new HttpParams().appendAll({ category: "" }));
    const promptItems = this.promptTreeService.buildHierarchy({
      models: promptModels,
      maxDepth: 2,
    });
    this.predefinedPrompts.push(...promptItems);
    await this.setFavoritesPrompts(this.predefinedPrompts);
    for (const node of this.predefinedPrompts) {
      if (node.children) {
        this.sortChildren(node.children);
      }
    }
  }

  async toggleFavorite(prompt?: PredefinedPrompt): Promise<void> {
    if (!prompt) {
      return;
    }
    prompt.isFavorite = !prompt.isFavorite;
    await this.promptsService.setFavoritePrompt(prompt.id, prompt.isFavorite);
    const favoritesNode = this.predefinedPrompts.find((node) => node.key === this.favoriteNodeKey);
    if (!favoritesNode?.children) {
      return;
    }
    if (prompt.isFavorite) {
      favoritesNode.children.push({ label: prompt.label, key: prompt.id, data: prompt });
    } else {
      favoritesNode.children = favoritesNode.children.filter((child) => child.key !== prompt.id);
    }
    // force the ui to update by changing the reference
    this.predefinedPrompts = cloneDeep(this.predefinedPrompts);
  }

  private async setFavoritesPrompts(nodes: TreeNode<PredefinedPrompt>[]): Promise<void> {
    // add the favorites node
    const favoritePromptsId = await this.promptsService.listFavoritesPrompts();
    const favoriteNode: TreeNode<PredefinedPrompt> = {
      key: this.favoriteNodeKey,
      label: $localize`:@@prompt_selector_category_label_favorites:Favoris`,
      selectable: false,
      children: [],
    };

    favoriteNode.children = [];
    this.addFavoriteMetaData(nodes, favoritePromptsId, favoriteNode);
    this.predefinedPrompts.unshift(favoriteNode);
  }

  private addFavoriteMetaData(
    nodes: TreeNode<PredefinedPrompt>[],
    favoritePromptsId: string[],
    favoriteNode: TreeNode<PredefinedPrompt>,
  ): void {
    //recursively set the isFavorite propery of each child node
    for (const node of nodes) {
      if (node.data && favoritePromptsId.includes(node.data.id)) {
        node.data.isFavorite = true;
        if (!favoriteNode.children) {
          favoriteNode.children = [];
        }
        favoriteNode.children.push({ label: node.data.label, key: node.data.id, data: node.data });
      }
      if (node.children) {
        this.addFavoriteMetaData(node.children, favoritePromptsId, favoriteNode);
      }
    }
  }

  favoriteButtonTooltip(node: TreeNode<PredefinedPrompt>): string {
    return node.data?.isFavorite
      ? $localize`:@@prompt_selector_favorite_button_tooltip_remove:Enlever des favoris`
      : $localize`:@@prompt_selector_favorite_button_tooltip_add:Ajouter aux favoris`;
  }

  private sortChildren(nodes: TreeNode<PredefinedPrompt>[]): void {
    // Recursively sort the children of a node
    nodes.sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
    for (const node of nodes) {
      if (node.children) {
        this.sortChildren(node.children);
      }
    }
  }

  protected openDeleteConfirmationDialog(parent?: TreeNode<PredefinedPrompt>, promptId?: string): void {
    const category = parent?.label as NamedPromptCategory;
    const parentId = parent!.data!.id;
    if (promptId) {
      this.confirmationService.confirm({
        message: $localize`:@@prompt_selector_delete_prompt_confirmation_message:Êtes-vous sûr de vouloir supprimer ce prompt ?`,
        header: $localize`:@@prompt_selector_delete_prompt_confirmation_title:Confirmation de suppression`,
        acceptLabel: $localize`:@@prompt_selector_delete_prompt_accept_button:Oui`,
        rejectLabel: $localize`:@@prompt_selector_delete_prompt_reject_button:Non`,
        icon: "pi pi-exclamation-triangle",
        accept: async () => {
          try {
            if (category === "MES_PROMPTS" || (await this.promptsService.isUserPrompt(parentId))) {
              await this.promptsService.deleteUserPrompt(promptId);
              this.promptsService.predefinedPromptsChanged.emit();
            } else {
              await this.promptsService.deleteTemplatePrompt(parentId, promptId);
              this.promptsService.predefinedPromptsChanged.emit();
            }
            this.notificationService.showInfo(
              $localize`:@@prompt_selector_delete_prompt_success_message:Prompt supprimé avec succès`,
            );
            this.selectedPrompt = undefined;
            this.confirmationService.close();
          } catch (err: unknown) {
            this.notificationService.showError(
              err,
              $localize`:@@prompt_selector_delete_prompt_error_message:Erreur lors de la suppression du prompt`,
            );
          }
        },
        reject: () => {
          this.confirmationService.close();
        },
      });
    }
  }

  protected copyPromptId(promptId?: string): void {
    copyStringToClipboard(promptId ?? "");
  }

  protected openPromptEditor(
    node: TreeNode<PredefinedPrompt> | undefined,
    currentPrompt: PredefinedPrompt | undefined,
    action: "add" | "edit" | "copy",
  ): void {
    const header =
      action === "add" || action === "copy"
        ? $localize`:@@prompt_selector_add_prompt_title:Ajouter un nouveau prompt à ${node?.label}`
        : $localize`:@@prompt_selector_edit_prompt_title:Éditer le prompt ${currentPrompt?.label}`;
    this.promptEditorDialogRef = this.dialogService.open(PromptEditorComponent, {
      header,
      width: "500px",
      resizable: true,
      draggable: true,
      data: {
        currentPrompt,
        promptCategory: node?.label as NamedPromptCategory,
        action,
        parent: node,
      },
    });
    this.promptEditorDialogRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((prompt) => {
      if (prompt) {
        this.promptsService.predefinedPromptsChanged.emit();
        const successMessage =
          action === "add"
            ? $localize`:@@prompt_selector_add_prompt_success_message:Prompt ajouté avec succès`
            : $localize`:@@prompt_selector_edit_prompt_success_message:Prompt édité avec succès`;
        this.notificationService.showInfo(successMessage);
        this.promptSelected.emit(prompt);
      }
    });
  }

  protected openNodeDeleteConfirmationDialog(node?: TreeNode<PredefinedPrompt>): void {
    if (node!.children!.length > 0) {
      this.notificationService.showError(
        $localize`:@@prompt_selector_remove_full_node_error:Impossible de supprimer un noeud contenant des enfants`,
        $localize`:@@prompt_selector_remove_full_node_error_message:Erreur lors de la suppression du noeud`,
      );
      return;
    }

    this.confirmationService.confirm({
      message: $localize`:@@prompt_selector_remove_node_confirmation_message:Êtes-vous sûr de vouloir supprimer ce noeud ?`,
      header: $localize`:@@prompt_selector_remove_node_confirmation_title:Confirmation de suppression`,
      acceptLabel: $localize`:@@prompt_selector_remove_node_accept_button:Oui`,
      rejectLabel: $localize`:@@prompt_selector_remove_node_reject_button:Non`,
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await this.promptsService.deleteTemplateNode(node!.data!.id);
          this.promptsService.predefinedPromptsChanged.emit();
          this.notificationService.showInfo(
            $localize`:@@prompt_selector_remove_node_success_message:Noeud supprimé avec succès`,
          );
          this.selectedPrompt = undefined;
          this.confirmationService.close();
        } catch (error) {
          this.notificationService.showError(
            error,
            $localize`:@@prompt_selector_remove_node_error_message:Erreur lors de la suppression du noeud`,
          );
        }
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  protected canSeeMoveNodeButton(node?: TreeNode<PredefinedPrompt>): boolean {
    const prompt = node?.data;
    if (!this.canModifyTemplatePrompts || !node?.parent || !prompt?.id || !prompt.label || prompt.label === "IAPP") {
      return false;
    }
    while (node) {
      if (node.data?.label === "SUMMARY") {
        return false;
      }
      node = node.parent;
    }
    return true;
  }

  protected canSeeMovePromptButton(node: TreeNode<PredefinedPrompt>): boolean {
    if (!this.canModifyTemplatePrompts) {
      return false;
    }
    let parent = node.parent;
    while (parent) {
      if (parent.data?.label === "SUMMARY") {
        return false;
      }
      parent = parent.parent;
    }
    return true;
  }

  protected openMoveNodeDialog(node: TreeNode<PredefinedPrompt> | undefined): void {
    const header = $localize`:@@prompt_selector_move_node_title:Déplacer le noeud ${node?.label}`;
    const promptNodeEditorDialogRef = this.dialogService.open(MoveDialogComponent, {
      header,
      width: "500px",
      data: { node, nodeType: "group" },
    });

    promptNodeEditorDialogRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((ok) => {
      if (ok) {
        this.promptsService.predefinedPromptsChanged.emit();
        const successMessage = $localize`:@@prompt_selector_sucessfully_moved_node_message:Noeud déplacé avec succès`;
        this.notificationService.showInfo(successMessage);
      }
    });
  }

  protected openMovePromptDialog(node: TreeNode<PredefinedPrompt> | undefined): void {
    const header = $localize`:@@prompt_selector_move_prompt_title:Déplacer le prompt ${node?.data?.label}`;
    this.promptEditorDialogRef = this.dialogService.open(MoveDialogComponent, {
      header,
      width: "500px",
      data: { node, nodeType: "prompt" },
    });
    this.promptEditorDialogRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((ok) => {
      if (ok) {
        this.promptsService.predefinedPromptsChanged.emit();
        const successMessage = $localize`:@@prompt_selector_move_prompt_success_message:Prompt déplacé avec succès`;
        this.notificationService.showInfo(successMessage);
      }
    });
  }

  protected canCopyToMyPrompts(node: TreeNode<PredefinedPrompt>): boolean {
    return !this.isParentMyPrompts(node) && !this.isParentMyFavorites(node);
  }

  protected async copyToMyPrompts(promptId?: string): Promise<void> {
    if (promptId) {
      try {
        await this.promptsService.copyToMyPrompts(promptId);
        await this.setPredefinedPrompts();
        this.notificationService.showInfo(
          $localize`:@@prompt_selector_copy_prompt_to_myprompts_success_message:Prompt copié vers Mes Prompts avec succès`,
        );
      } catch (error) {
        this.notificationService.showError(
          error,
          $localize`:@@prompt_selector_copy_prompt_to_myprompts_error_message:Erreur lors de la copie du prompt`,
        );
      }
    }
  }

  protected sharePrompt(promptId?: string): void {
    if (promptId) {
      // Retrieve current URL path
      const currentUrl = window.location.href;
      let newUrl = currentUrl;
      // Add promptId to the URL
      if (currentUrl.includes("?")) {
        const url = new URL(currentUrl);
        const params = new URLSearchParams(url.search);
        params.set("promptId", promptId);
        url.search = params.toString();
        newUrl = url.toString();
      } else {
        newUrl = `${currentUrl}?promptId=${promptId}`;
      }
      copyStringToClipboard(newUrl);
      this.notificationService.showInfo(
        $localize`:@@prompt_selector_share_prompt_success_message:Lien vers le prompt copié dans le presse-papier`,
      );
    }
  }

  protected isMyPrompts(node: TreeNode<PredefinedPrompt>): boolean {
    return node?.label === this.categories.MES_PROMPTS.label;
  }

  protected isParentMyPrompts(node: TreeNode<PredefinedPrompt>): boolean {
    let parent = node.parent;
    while (parent) {
      if (parent?.label === this.categories.MES_PROMPTS.label) {
        return true;
      }
      parent = parent.parent;
    }
    return false;
  }

  protected isMyFavorites(node?: TreeNode<PredefinedPrompt>): boolean {
    return node?.key === this.favoriteNodeKey;
  }

  protected isParentMyFavorites(node?: TreeNode<PredefinedPrompt>): boolean {
    let parent = node?.parent;
    while (parent) {
      if (parent?.key === this.favoriteNodeKey) {
        return true;
      }
      parent = parent.parent;
    }
    return false;
  }
}
